import { useState, useEffect, useRef, useCallback } from 'react';
import { cloudFunctionV2, checkOnAuthStateChanged, regenerateTimeline } from '../functions';
import { buildSubjectIdentity, excludeSubjectComps } from '../utils/subjectSelfComp';

// Lazy fetcher for the AWS-gateway comp universe (recently-sold + for-sale)
// for a given property. Owned at the page level so its state survives
// ArvWorkspace unmount/remount when the user toggles between tabs — first
// ARV-tab visit fires the fetch, subsequent visits read from cache.
//
// Lazy by design: `loadComps()` must be called by the consumer (typically
// from a useEffect on first mount of the consuming component). The hook
// itself doesn't fetch on mount, so leads where the user never opens the
// ARV tab don't pay the round-trip cost.
//
// Resets state when propertyId changes (navigating to a different lead).
// Resets the "has fetched" guard on errors so the next loadComps() call
// retries instead of returning the stale error state.
export function useLeadComps({ companyId, propertyId, countyId, compsSnapshot, compFetcherV1FilterReport }) {
    // compsSnapshot (data.arvData.compsSnapshot on the property doc) marks a
    // compFetcherV1-era report whose comps were frozen to GCS. When present we
    // serve from the stored snapshot (getStoredComps) instead of re-pulling the
    // live, non-deterministic data; absent → legacy AWS-gateway re-pull.
    //
    // V1 leads must NEVER fall back to AWS — the two sources disagree per-field
    // (e.g. compDetails.propertyCondition) and mixing them produces silent
    // inconsistencies between the ARV math (V1) and the displayed comp labels
    // (AWS). compFetcherV1FilterReport is a secondary "V1 ran" marker that
    // catches a transitional state where the snapshot wasn't written.
    const hasSnapshot = compsSnapshot?.source === 'compFetcherV1';
    const ranOnV1 = hasSnapshot || !!compFetcherV1FilterReport;
    const [allComps, setAllComps] = useState(null);
    const [subjectProperty, setSubjectProperty] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const fetchedRef = useRef(false);
    // Mirror of propertyId in a ref so a fetch in flight can detect mid-flight
    // navigation to a different lead and skip its setState calls — otherwise
    // the old fetch's setAllComps would briefly paint lead B's workspace with
    // lead A's comps before B's fetch completes.
    const propertyIdRef = useRef(propertyId);
    useEffect(() => { propertyIdRef.current = propertyId; }, [propertyId]);

    // Reset on propertyId change AND when hasSnapshot flips. The latter matters
    // because propertyData (carrying compsSnapshot) can load AFTER the first
    // loadComps() fires: without this, an early gateway fetch would set
    // fetchedRef=true and the snapshot path would never run for a compFetcher
    // lead. Re-resetting when the snapshot flag arrives lets it take over.
    //
    // ranOnV1 must also reset: a V1 lead can arrive at the hook with
    // compFetcherV1FilterReport unset (propertyData still loading) → ranOnV1
    // false → the early AWS gateway fetch wins → fetchedRef locks the AWS data
    // in even when FilterReport later arrives. Adding ranOnV1 to the deps
    // re-fires the reset when V1-ness becomes known, and loadComps' own deps
    // (further down) re-invoke the fetcher with the correct branch.
    useEffect(() => {
        fetchedRef.current = false;
        setAllComps(null);
        setSubjectProperty(null);
        setError(false);
        setLoading(false);
    }, [propertyId, hasSnapshot, ranOnV1]);

    const loadComps = useCallback(async () => {
        if (fetchedRef.current) return;
        // companyId/propertyId can be transiently undefined while the lead
        // loads — silently wait and let the consumer's effect re-fire.
        if (!companyId || !propertyId) return;
        // countyId is a hard requirement for the AWS gateway, but NOT for the
        // stored-comps snapshot path. Only block on it when we'd hit the gateway.
        if (!countyId && !hasSnapshot) {
            console.warn('[useLeadComps] countyId missing on lead — cannot fetch comps', { propertyId });
            fetchedRef.current = true;
            setError(true);
            return;
        }
        const fetchPropertyId = propertyId;
        fetchedRef.current = true;
        setLoading(true);
        setError(false);
        try {
            const auth = await checkOnAuthStateChanged();
            if (auth?.status !== 200 || !auth?.userId) {
                throw new Error('Auth check failed');
            }
            // Snapshot-first: for compFetcherV1-era reports, read the frozen
            // comp set from GCS via getStoredComps.
            let data = null;
            if (hasSnapshot && process.env.REACT_APP_GET_STORED_COMPS_URL) {
                try {
                    const snapRes = await cloudFunctionV2(
                        process.env.REACT_APP_GET_STORED_COMPS_URL,
                        { companyId, propertyId: String(propertyId) }
                    );
                    if (snapRes?.status === 200) {
                        data = snapRes.body?.data || snapRes.body;
                    } else {
                        console.warn(`[useLeadComps] stored-comps returned ${snapRes?.status} for V1 lead`);
                    }
                } catch (snapErr) {
                    console.warn('[useLeadComps] stored-comps fetch failed:', snapErr?.message);
                }
            }
            // V1 ran but snapshot is missing — refuse the AWS gateway fallback.
            // Silently switching to AWS produces the per-field mismatch bug
            // (subject 21306367: V1 had condition=null, AWS labeled it WM).
            if (!data && ranOnV1) {
                throw new Error('V1 ran but snapshot unavailable — re-run the report to regenerate it');
            }
            if (!data) {
                if (!countyId) throw new Error('countyId required for AWS gateway fallback');
                const gatewayURL = process.env.REACT_APP_AWS_QUERY_URL;
                const params = {
                    type: 'lambdaDirect',
                    lambdaParams: {
                        propertyId: String(propertyId),
                        userId: auth.userId,
                        countyId: String(countyId),
                    },
                };
                const result = await cloudFunctionV2(gatewayURL, params);
                if (result?.status !== 200) {
                    throw new Error(`Gateway returned status ${result?.status}`);
                }
                data = result.body?.data || result.body;
            }
            const compsBlock = data?.salesComparables?.comps;
            const recentlySold = compsBlock?.recentlySold || [];
            const forSale = compsBlock?.forSale || [];
            // The same gateway response carries the subject property in AWS-comp
            // shape (addressDetails, propertyAttributes, listingDetails) — surface
            // it so the ARV workspace's map + subject card don't need a second
            // join from the lead doc's flatter fields.
            const subject = data?.propertyDetails || null;
            const raw = [...recentlySold, ...forSale]
                .map(item => item?.propertyDetails)
                .filter(Boolean);
            // The subject property can appear in its OWN comp pool: it is a real
            // record inside the search polygon, and while it is listed it sits in
            // the `forSale` bucket like any other active. A house is not a comp
            // for itself — the "comp" would just be the ask we're testing — so it
            // never reaches the workspace: not the map, not the rail, not the
            // counts, and not the comps the user can hand-pick into ARV /
            // rent-ready / as-is. Filtered here rather than in ArvWorkspace so
            // every consumer of this hook sees the same pool.
            //
            // `propertyId` is passed as a fallback id because the AWS-gateway
            // branch can return a response with no `propertyDetails` at all —
            // without a subject record the zpid/address signals are unavailable,
            // but the lead's own ReAPI propertyId still matches a self-comp.
            const { kept: flat, dropped: subjectComps } = excludeSubjectComps(
                raw,
                buildSubjectIdentity(subject, propertyId)
            );
            if (subjectComps.length) {
                console.warn(`[useLeadComps] dropped ${subjectComps.length} comp(s) that are the subject property itself`, { propertyId });
            }
            // Skip the state set if the user has navigated to a different lead
            // since this fetch started — the new lead's fetch is the source of
            // truth, painting its result here would briefly show the wrong data.
            if (propertyIdRef.current !== fetchPropertyId) return;
            await regenerateTimeline(flat);
            setAllComps(flat);
            setSubjectProperty(subject);
        } catch (err) {
            console.error('useLeadComps fetch failed:', err);
            // Same nav-guard as the success path: don't set the error state on
            // a stale fetch — it would override the new fetch's loading state.
            if (propertyIdRef.current !== fetchPropertyId) return;
            setError(true);
            // Allow next loadComps() call to retry — transient failures
            // (auth flake, gateway 5xx) shouldn't permanently block the user.
            fetchedRef.current = false;
        } finally {
            // Only clear loading if we're still on the same lead. Otherwise the
            // new fetch's setLoading(true) is authoritative and we'd flicker.
            if (propertyIdRef.current === fetchPropertyId) setLoading(false);
        }
    }, [companyId, propertyId, countyId, hasSnapshot, ranOnV1]);

    return { allComps, subjectProperty, loading, error, loadComps };
}
